const CACHE_NAME = 'matrix-media-v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const CACHED_AT_HEADER = 'X-Cached-At';
const MAX_CONCURRENT_BULK_DOWNLOADS = 2;
const PLAINTEXT_CACHE_BUDGET_BYTES = 200 * 1024 * 1024;

/** Cache API requires HTTP(S) URLs — convert mxc://server/id to a synthetic HTTPS key */
function toCacheKey(mxcUrl: string): string {
    return `https://matrix-media-cache/${mxcUrl.replace('mxc://', '')}`;
}

function createAbortError(): Error {
    const error = new Error('Media download aborted');
    error.name = 'AbortError';
    return error;
}

export interface MediaFetchOptions {
    lane?: 'bulk';
    signal?: AbortSignal;
}

interface InFlightFetch {
    promise: Promise<Blob>;
    controller: AbortController;
    consumers: number;
}

interface QueueWaiter {
    start: () => void;
}

class DownloadQueue {
    private active = 0;
    private waiting: QueueWaiter[] = [];

    constructor(private readonly limit: number) {}

    async run<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
        if (signal?.aborted) throw createAbortError();

        if (this.active >= this.limit) {
            await new Promise<void>((resolve, reject) => {
                const onAbort = () => {
                    this.waiting = this.waiting.filter(waiter => waiter !== queued);
                    reject(createAbortError());
                };
                const queued: QueueWaiter = {
                    start: () => {
                        signal?.removeEventListener('abort', onAbort);
                        resolve();
                    },
                };
                this.waiting.push(queued);
                signal?.addEventListener('abort', onAbort, { once: true });
            });
        } else {
            this.active += 1;
        }

        try {
            return await task();
        } finally {
            const next = this.waiting.shift();
            if (next) {
                next.start();
            } else {
                this.active -= 1;
            }
        }
    }
}

export class MediaCacheService {
    private inFlight = new Map<string, InFlightFetch>();
    private plaintextCache = new Map<string, { blob: Blob; cachedAt: number }>();
    private plaintextBytes = 0;
    private bulkQueue = new DownloadQueue(MAX_CONCURRENT_BULK_DOWNLOADS);
    private plaintextInFlight = new Map<string, Promise<Blob>>();
    private generation = 0;

    constructor(private readonly plaintextBudgetBytes = PLAINTEXT_CACHE_BUDGET_BYTES) {
        if (typeof caches !== 'undefined') {
            setTimeout(() => this.evictExpired(), 10_000);
            setInterval(() => this.evictExpired(), CLEANUP_INTERVAL_MS);
        }
    }

    private get isAvailable(): boolean {
        return typeof caches !== 'undefined';
    }

    async getOrFetchBlob(
        mxcUrl: string,
        httpUrl: string,
        token: string,
        options: MediaFetchOptions = {},
    ): Promise<Blob> {
        let entry = this.inFlight.get(mxcUrl);
        if (!entry) {
            const controller = new AbortController();
            const promise = this.fetchWithCache(
                mxcUrl,
                httpUrl,
                token,
                this.generation,
                options.lane,
                controller.signal,
            );
            const created: InFlightFetch = { promise, controller, consumers: 0 };
            entry = created;
            this.inFlight.set(mxcUrl, created);
            const forget = () => {
                if (this.inFlight.get(mxcUrl) === created) {
                    this.inFlight.delete(mxcUrl);
                }
            };
            promise.then(forget, forget);
        }

        return this.awaitFetch(mxcUrl, entry, options.signal);
    }

    private awaitFetch(mxcUrl: string, entry: InFlightFetch, signal?: AbortSignal): Promise<Blob> {
        entry.consumers += 1;

        return new Promise<Blob>((resolve, reject) => {
            let released = false;

            const release = (abortIfUnused: boolean) => {
                if (released) return;
                released = true;
                signal?.removeEventListener('abort', onAbort);
                entry.consumers -= 1;
                if (abortIfUnused && entry.consumers <= 0) {
                    if (this.inFlight.get(mxcUrl) === entry) {
                        this.inFlight.delete(mxcUrl);
                    }
                    entry.controller.abort();
                }
            };

            const onAbort = () => {
                release(true);
                reject(createAbortError());
            };

            if (signal?.aborted) {
                onAbort();
                return;
            }
            signal?.addEventListener('abort', onAbort, { once: true });

            entry.promise.then(
                blob => {
                    release(false);
                    resolve(blob);
                },
                error => {
                    release(false);
                    reject(error);
                },
            );
        });
    }

    async getOrCreatePlaintextBlob(cacheKey: string, factory: () => Promise<Blob>): Promise<Blob> {
        const cached = this.plaintextCache.get(cacheKey);
        if (cached && Date.now() - cached.cachedAt < TTL_MS) {
            this.plaintextCache.delete(cacheKey);
            this.plaintextCache.set(cacheKey, cached);
            return cached.blob;
        }
        if (cached) {
            this.removePlaintext(cacheKey);
        }

        const existing = this.plaintextInFlight.get(cacheKey);
        if (existing) return existing;

        const generation = this.generation;
        const promise = factory();
        this.plaintextInFlight.set(cacheKey, promise);

        try {
            const blob = await promise;
            if (generation === this.generation) {
                this.storePlaintext(cacheKey, blob);
            }
            return blob;
        } finally {
            if (this.plaintextInFlight.get(cacheKey) === promise) {
                this.plaintextInFlight.delete(cacheKey);
            }
        }
    }

    private removePlaintext(cacheKey: string): void {
        const entry = this.plaintextCache.get(cacheKey);
        if (!entry) return;
        this.plaintextCache.delete(cacheKey);
        this.plaintextBytes -= entry.blob.size;
    }

    private storePlaintext(cacheKey: string, blob: Blob): void {
        this.removePlaintext(cacheKey);
        if (blob.size > this.plaintextBudgetBytes) return;

        this.plaintextCache.set(cacheKey, { blob, cachedAt: Date.now() });
        this.plaintextBytes += blob.size;

        for (const key of this.plaintextCache.keys()) {
            if (this.plaintextBytes <= this.plaintextBudgetBytes) break;
            this.removePlaintext(key);
        }
    }

    private async fetchWithCache(
        mxcUrl: string,
        httpUrl: string,
        token: string,
        generation: number,
        lane: 'bulk' | undefined,
        signal: AbortSignal,
    ): Promise<Blob> {
        const cacheKey = toCacheKey(mxcUrl);

        if (this.isAvailable && generation === this.generation) {
            try {
                const cache = await caches.open(CACHE_NAME);
                const cached = await cache.match(cacheKey);
                if (cached) {
                    const cachedAt = Number(cached.headers.get(CACHED_AT_HEADER));
                    if (!cachedAt || Date.now() - cachedAt < TTL_MS) {
                        console.debug('[MediaCache] HIT', mxcUrl);
                        return await cached.blob();
                    }
                    console.debug('[MediaCache] EXPIRED', mxcUrl);
                    await cache.delete(cacheKey);
                }
            } catch {
                // Fall through to network fetch
            }
        }

        const download = async () => {
            const response = await fetch(httpUrl.replace('media/v3', 'client/v1/media'), {
                headers: { 'Authorization': `Bearer ${token}` },
                signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch media: ${response.status}`);
            }

            return response.blob();
        };
        const blob = lane === 'bulk' ? await this.bulkQueue.run(download, signal) : await download();
        console.debug('[MediaCache] MISS — fetched from network', mxcUrl);

        if (this.isAvailable && generation === this.generation) {
            try {
                const cache = await caches.open(CACHE_NAME);
                const cacheResponse = new Response(blob, {
                    headers: {
                        'Content-Type': blob.type,
                        [CACHED_AT_HEADER]: String(Date.now()),
                    },
                });
                await cache.put(cacheKey, cacheResponse);
            } catch {
                // Cache write failed, blob is still valid
            }
        }

        return blob;
    }

    private async evictExpired(): Promise<void> {
        const now = Date.now();
        for (const [key, entry] of this.plaintextCache.entries()) {
            if (now - entry.cachedAt >= TTL_MS) {
                this.removePlaintext(key);
            }
        }

        if (!this.isAvailable) return;

        try {
            const cache = await caches.open(CACHE_NAME);
            const keys = await cache.keys();

            for (const request of keys) {
                const response = await cache.match(request);
                if (!response) continue;

                const cachedAt = Number(response.headers.get(CACHED_AT_HEADER));
                if (cachedAt && now - cachedAt >= TTL_MS) {
                    await cache.delete(request);
                }
            }
        } catch {
            // Ignore cleanup errors
        }
    }

    async clearCache(): Promise<void> {
        this.generation += 1;
        this.inFlight.clear();
        this.plaintextCache.clear();
        this.plaintextBytes = 0;
        this.plaintextInFlight.clear();

        if (this.isAvailable) {
            try {
                await caches.delete(CACHE_NAME);
            } catch {
                // Ignore cleanup errors
            }
        }
    }

    async removeCachedMedia(mxcUrl: string): Promise<void> {
        if (this.isAvailable) {
            try {
                const cache = await caches.open(CACHE_NAME);
                await cache.delete(toCacheKey(mxcUrl));
            } catch {
                // Ignore cleanup errors
            }
        }
    }
}

export const mediaCacheService = new MediaCacheService();
